import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { Box, Slider, Typography } from "@mui/material";

import { weatherIcons, weatherIconsLabels } from "assets/weather/weather-icons";
import { WeatherInterval, WeatherMain } from "api/event";
import styles from "./weather.module.scss";

dayjs.extend(utc);

function valuetext(value: number) {
  return `${value} hour`;
}

interface WeatherTemperatureProps {
  interval: WeatherInterval;
}

/**
 * Renders weather temperature for the selected hour
 *
 * @param props
 * @returns
 */
function WeatherTemperature(props: WeatherTemperatureProps): JSX.Element {
  const { interval } = props;

  return (
    <Box display='flex' alignItems='flex-start'>
      <span className={styles.temperatureValue}>{Math.round(interval.temp)}</span>
      <span>&#8451;</span>
    </Box>
  );
}

interface WeatherDetailsProps {
  interval: WeatherInterval;
}

/**
 * Renders weather details like precipitation, humidity and wind speed
 *
 * @param props
 * @returns
 */
function WeatherDetails(props: WeatherDetailsProps): JSX.Element {
  const { interval } = props;

  return (
    <Box display='flex' flexDirection='column'>
      <Typography color='text.secondary' variant='caption'>
        {`Precip: ${interval.precip}%`}
      </Typography>
      <Typography color='text.secondary' variant='caption'>
        {`Humidity: ${interval.humidity}%`}
      </Typography>
      <Typography color='text.secondary' variant='caption'>
        {`Wind: ${interval.wind.toFixed(1)} km/h`}
      </Typography>
    </Box>
  );
}

interface HoursSliderProps {
  intervals: WeatherInterval[];
  selectedHour: number;
  onSelectHour: (value: number) => void;
}

/**
 * Generate marks for hours slider
 *
 * @param intervals
 * @returns
 */
function generateSliderMarks(intervals: WeatherInterval[]): {
  value: number;
  label: string;
}[] {
  const marks: { value: number; label: string }[] = [];

  if (intervals.length >= 1 && intervals.length <= 5) {
    intervals.forEach((interval, index) => {
      marks.push({
        value: index,
        label: dayjs.unix(interval.dt).utcOffset(0).format("HH:mm"),
      });
    });
  } else {
    intervals.forEach((interval, index) => {
      if (index % 2 !== 0) {
        marks.push({
          value: index,
          label: dayjs.unix(interval.dt).utcOffset(0).format("HH:mm"),
        });
      }
    });
  }

  return marks;
}

/**
 * Renders the slider to select hours
 *
 * @param props
 * @returns
 */
function HoursSlider(props: HoursSliderProps): JSX.Element | null {
  const { intervals, selectedHour, onSelectHour } = props;
  const marks: { value: number; label: string }[] = generateSliderMarks(intervals);

  function handleChangeHour(event: globalThis.Event, value: number | number[]) {
    if (typeof value === "number") {
      onSelectHour(value);
    }
  }

  return intervals.length > 1 ? (
    <Slider
      aria-label='Weather by hours'
      data-testid='hours-slider'
      getAriaValueText={valuetext}
      marks={marks}
      max={intervals.length - 1}
      min={0}
      onChange={handleChangeHour}
      step={1}
      value={selectedHour}
    />
  ) : null;
}

interface WeatherDayProps {
  dayData: WeatherInterval[];
  order: number;
  selected?: boolean;
  onSelect: (order: number) => void;
}

/**
 * Renders available days which can be selected to see weather details
 *
 * @param props
 * @returns
 */
function WeatherDay(props: WeatherDayProps): JSX.Element {
  const { dayData, order, selected, onSelect } = props;

  // Calculate minimum, maximum temperature and main temperature
  let minTemp = dayData[0].temp;
  let maxTemp = dayData[0].temp;
  const mainTemperatures: Partial<{ [K in WeatherMain]: number }> = {};

  dayData.forEach((interval) => {
    mainTemperatures[interval.main] = mainTemperatures[interval.main] ? mainTemperatures[interval.main]! + 1 : 1;

    if (interval.temp < minTemp) {
      minTemp = interval.temp;
    }
    if (interval.temp > maxTemp) {
      maxTemp = interval.temp;
    }
  });

  minTemp = Math.round(minTemp);
  maxTemp = Math.round(maxTemp);
  const mainTemperature = Object.keys(mainTemperatures).reduce((a, b) =>
    mainTemperatures[a as WeatherMain]! > mainTemperatures[b as WeatherMain]! ? a : b
  ) as WeatherMain;

  return (
    <button className={styles.weatherDayBtn} type='button' onClick={() => onSelect(order)}>
      <Typography
        className={classNames(styles.weatherDay, {
          [styles.selectedWeatherDay]: selected,
        })}
        variant='overline'
      >
        {dayjs.unix(dayData[0].dt).utcOffset(0).format("ddd")}
      </Typography>
      <img src={weatherIcons[mainTemperature]} alt={weatherIconsLabels[mainTemperature]} width={48} height={48} />
      <Typography variant='caption'>{maxTemp}&#8451;</Typography>
      <Typography color='text.secondary' variant='caption'>
        {minTemp}&#8451;
      </Typography>
    </button>
  );
}

interface DisabledWeatherDayProps {
  date: dayjs.Dayjs;
}

function DisabledWeatherDay(props: DisabledWeatherDayProps): JSX.Element {
  const { date } = props;

  return (
    <button className={styles.weatherDayBtn} disabled type='button'>
      <Typography className={styles.weatherDay} color='text.secondary' variant='overline'>
        {date.format("ddd")}
      </Typography>
      <img src={weatherIcons.Clouds} alt={weatherIconsLabels.Clouds} width={48} height={48} />
      <Typography color='text.secondary' variant='caption'>
        Not
        <br />
        available
      </Typography>
    </button>
  );
}

export interface WeatherProps {
  data?: WeatherInterval[][];
  eventLength: number;
  placeholders: dayjs.Dayjs[];
  onChangeWeather: (value: WeatherInterval) => void;
}

/**
 * Renders weather component
 *
 * @param props
 * @returns
 */
function Weather(props: WeatherProps): JSX.Element | null {
  const { eventLength, data, placeholders, onChangeWeather } = props;
  const [selectedDay, setSelectedDay] = useState<number>(0);
  const [selectedHour, setSelectedHour] = useState<number>(0);

  /**
   * When day is changed always set first hour of day
   */
  useEffect(() => {
    setSelectedHour(0);
  }, [selectedDay]);

  /**
   * call [onChangeWeather] whenever the day or the hour changes
   */
  useEffect(() => {
    if (data) {
      onChangeWeather(data[selectedDay][selectedHour]);
    }
  }, [selectedDay, selectedHour]);

  /**
   * Reset selected day and hour if weather data changes
   */
  useEffect(() => {
    setSelectedDay(0);
    setSelectedHour(0);
  }, [data]);

  // On desktop, because Event container is not unmounted when an event is selected
  // the selected day an hour remain the same until the useEffect above runs to reset them
  if (data && !data[selectedDay][selectedHour]) {
    return null;
  }

  return data ? (
    <Box>
      <Box display='flex' justifyContent='space-between' mt={2}>
        <WeatherTemperature interval={data[selectedDay][selectedHour]} />
        <WeatherDetails interval={data[selectedDay][selectedHour]} />
      </Box>
      <Box mt={1} mx={2}>
        <HoursSlider
          selectedHour={selectedHour}
          intervals={data[selectedDay]}
          onSelectHour={(value) => setSelectedHour(value)}
        />
      </Box>

      {eventLength > 1 ? (
        <Box className={styles.weatherDaysContainer}>
          {data.map((dayData, i) => (
            <WeatherDay
              dayData={dayData}
              order={i}
              key={`weather-day-${i}`}
              selected={i === selectedDay}
              onSelect={(order) => setSelectedDay(order)}
            />
          ))}
          {placeholders.map((placeholder, i) => (
            <DisabledWeatherDay date={placeholder} key={`weather-day-placeholder-${i}`} />
          ))}
        </Box>
      ) : null}
    </Box>
  ) : (
    <Box display='flex' justifyContent='center' my={3}>
      <Typography className={styles.weatherPlaceholder} color='text.secondary'>
        The weather will be available 5 days before the start of the event
      </Typography>
    </Box>
  );
}

export default Weather;
